import { Briefcase, GearSix, SignOut, User, X } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

import AimfoxAvatar from "common/components/ui/AimfoxAvatar";
import { Button } from "common/components/ui/Button";
import { Sheet, SheetContent, SheetTrigger } from "common/components/ui/Sheet";
import { SheetClose } from "common/components/ui/Sheet/Sheet";
import { Tag } from "common/components/ui/Tag";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useCustomer from "common/datahooks/useCustomer";
import { getWhitelabelCustomization } from "common/helpers/utils";
import ExtraNavItems from "common/layouts/WorkspaceLayout/ExtraNavItems";
import type { NavItemType } from "common/types";

import LogOutModal from "../LogOutModal";
import NavItem from "../NavItem";

import AimfoxLogoOrange from "assets/aimfoxLogos/aimfox-logo-orange.svg?react";
import HamburgerIcon from "assets/icons/hamburger.svg?react";

export default function MobileNav({ navItems }: { navItems: NavItemType[] }) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const [isOpen, setIsOpen] = useState(false);
  const [isLogOutOpen, setIsLogOutOpen] = useState(false);
  const location = useLocation();
  const { whitelabelCustomization } = getWhitelabelCustomization();

  const { customerInfo } = useCustomer();
  const { first_name: firstName, last_name: lastName, email } = customerInfo;

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  function onLogOutItemClick() {
    setIsLogOutOpen(true);
    setIsOpen(false);
  }

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button size="lg" variant="outline-black" intent="iconOnly">
            <HamburgerIcon />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="right"
          className="m-0 flex w-10/12 flex-col rounded-l-3xl rounded-r-none rounded-bl-none bg-black-950 px-4 py-8"
        >
          <div className="flex justify-between">
            {whitelabelCustomization ? (
              <img
                src={whitelabelCustomization.logo}
                alt=""
                className="max-h-10"
              />
            ) : (
              <div className="flex items-end">
                <AimfoxLogoOrange className="w-8" />
                <span className="mb-0.5 text-lg-bold text-white">Aimfox</span>
              </div>
            )}
            <SheetClose>
              <X className="fill-white" size={24} />
            </SheetClose>
          </div>
          <div className="flex gap-3 py-10">
            <AimfoxAvatar size="lg" firstName={firstName} lastName={lastName} />
            <div className="flex flex-col">
              <span className="line-clamp-1 break-all text-xl-bold text-white">
                {firstName} {lastName}
              </span>
              <span className="line-clamp-1 break-all text-black-400">
                {email}
              </span>
            </div>
          </div>
          <div className="overflow-y-auto scrollbar-thin gap-y-1 flex flex-col">
            {navItems.map(({ path, label, icon, notifications }) => (
              <NavLink to={path} key={path}>
                {({ isActive }) => (
                  <NavItem
                    isActive={isActive}
                    icon={icon}
                    isOnboarding={path === "onboarding"}
                    isExpanded
                  >
                    {label}
                    {!!notifications && (
                      <Tag className="ml-auto" variant="primary">
                        {notifications}
                      </Tag>
                    )}
                  </NavItem>
                )}
              </NavLink>
            ))}

            <div className="my-4 h-px w-full shrink-0 bg-black-500" />

            <NavLink
              to={`/workspaces/${workspaceId}/settings`}
              end
              state={{ canGoBack: true }}
            >
              {({ isActive }) => (
                <NavItem
                  icon={
                    whitelabelCustomization ? (
                      <GearSix weight="fill" />
                    ) : (
                      <User weight="fill" />
                    )
                  }
                  isActive={isActive}
                  isExpanded
                >
                  {whitelabelCustomization ? "Settings" : "Profile settings"}
                </NavItem>
              )}
            </NavLink>

            {!whitelabelCustomization && (
              <>
                <NavLink to={`/workspaces/${workspaceId}/settings/agency`} end>
                  {({ isActive }) => (
                    <NavItem
                      icon={<Briefcase weight="fill" />}
                      isActive={isActive}
                      isExpanded
                    >
                      Agency plan
                    </NavItem>
                  )}
                </NavLink>
                <ExtraNavItems isExpanded />
              </>
            )}

            <button type="button" onClick={onLogOutItemClick}>
              <NavItem icon={<SignOut />} isExpanded>
                Logout
              </NavItem>
            </button>
          </div>
        </SheetContent>
      </Sheet>
      <LogOutModal isOpen={isLogOutOpen} setIsOpen={setIsLogOutOpen} />
    </>
  );
}
